import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { useReducedMotion } from '../../design/motion';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import type { RecorderPhase } from './recorder';
import s from './Voice.module.css';

// APP-104 §3.2: the slim recording bar. Every word of copy here is fixed by the spec. It carries
// `data-feedback-ui`, so the page's picture (drawn once this has painted) leaves it out.

export const STARTING = 'Starting the microphone…';
export const RECORDING = 'Recording';
export const FINISHING = 'Finishing the transcript…';

/** The level bars' shape, middle tallest (seven, §3.2). */
const SHAPE = [0.45, 0.7, 0.9, 1, 0.9, 0.7, 0.45] as const;
/** Under this many seconds of voice left today, the bar says about how many (controller ruling). */
const TIME_LEFT_LINE_S = 300;

/** The bar's line about the day's voice time, or null with plenty left. */
export function voiceTimeLeft(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || seconds >= TIME_LEFT_LINE_S) return null;
  if (seconds < 60) return 'Less than a minute of voice left today';
  return `About ${String(Math.floor(seconds / 60))} min of voice left today`;
}

/** Still bars: the shape at rest. */
export const STILL = SHAPE.map((v) => `scaleY(${String(0.25 + v * 0.35)})`);

export interface RecordingBarProps {
  phase: RecorderPhase;
  /** When the microphone was first heard (ms), for the timer. */
  since: number | null;
  /** The loudness, 0 to 1, written as frames arrive and read here once a frame. */
  level: { current: number };
  /** The last finished phrase: announced once, politely. */
  lastPhrase: string;
  /** How many phrases so far: a phrase said twice is announced twice. */
  phraseCount: number;
  /** The phrase being spoken: shown, not announced. */
  partial: string;
  /** Voice time left today when recording began (seconds), or null when unknown. */
  secondsLeft: number | null;
  onDone: () => void;
  onTypeInstead: () => void;
  onCancel: () => void;
}

export const clock = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1_000));
  return `${String(Math.floor(total / 60))}:${String(total % 60).padStart(2, '0')}`;
};

/** Time recorded, ticking while recording (it stops at Done). Help chat's strip shares it. */
export function useElapsed(phase: RecorderPhase, since: number | null): number {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (phase !== 'recording' || since === null) return;
    const tick = (): void => {
      setElapsed(Date.now() - since);
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => {
      window.clearInterval(timer);
    };
  }, [phase, since]);
  return elapsed;
}

/**
 * The level bars follow the sound, unless motion is reduced: then they stay as drawn. Help chat's
 * strip shares it.
 */
export function useLevelBars(
  levels: RefObject<HTMLSpanElement | null>,
  level: { current: number },
  phase: RecorderPhase,
  still: boolean,
): void {
  useEffect(() => {
    if (still || phase !== 'recording') return;
    let frame = 0;
    let shown = 0;
    const draw = (): void => {
      shown = Math.max(level.current, shown * 0.86);
      level.current *= 0.86;
      const bars = levels.current?.children;
      if (bars !== undefined)
        for (const [i, bar] of [...bars].entries()) {
          const target = shown * (SHAPE[i] ?? 0.5) * (0.82 + Math.random() * 0.36);
          (bar as HTMLElement).style.transform = `scaleY(${String(
            Math.max(0.2, Math.min(1, target)),
          )})`;
        }
      frame = window.requestAnimationFrame(draw);
    };
    frame = window.requestAnimationFrame(draw);
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [still, phase, level, levels]);
}

export function RecordingBar({
  phase,
  since,
  level,
  lastPhrase,
  phraseCount,
  partial,
  secondsLeft,
  onDone,
  onTypeInstead,
  onCancel,
}: RecordingBarProps): ReactElement {
  const still = useReducedMotion();
  const region = useRef<HTMLElement>(null);
  const levels = useRef<HTMLSpanElement>(null);
  const line = useRef<HTMLParagraphElement>(null);
  // The timer runs while recording and stops at Done.
  const elapsed = useElapsed(phase, since);

  // Keyboard and screen-reader users land on the bar, so Done is one Tab away.
  useEffect(() => {
    region.current?.focus({ preventScroll: true });
  }, []);

  // One line of words: when they no longer fit, the newest stay in view and the start fades out.
  useLayoutEffect(() => {
    const el = line.current;
    if (el === null) return;
    el.scrollLeft = el.scrollWidth;
    el.dataset['overflowing'] = String(el.scrollWidth > el.clientWidth);
  }, [lastPhrase, partial]);

  useLevelBars(levels, level, phase, still);

  const words = [lastPhrase, partial].filter((w) => w !== '');
  // The day's time counts down with the recording.
  const timeLeft = voiceTimeLeft(
    secondsLeft === null ? null : Math.max(0, secondsLeft - Math.floor(elapsed / 1_000)),
  );
  return (
    <section
      ref={region}
      tabIndex={-1}
      aria-label="Voice feedback"
      className={s.bar}
      data-feedback-ui
    >
      <div className={s.inner}>
        {/* The words as they come, one line; the live region below announces finished phrases. */}
        <p ref={line} className={s.words} data-testid="voice-words">
          {words.length > 0 && (
            <span>
              {lastPhrase}
              {partial !== '' && <span className={s.partial}> {partial}</span>}
            </span>
          )}
        </p>
        <p className="visually-hidden" aria-live="polite">
          {/* A new node for each phrase, so the same words said again are read again. */}
          <span key={phraseCount}>{lastPhrase}</span>
        </p>
        <div className={s.row}>
          <div className={s.state}>
            {phase === 'recording' ? (
              <span
                className={s.dot}
                data-recording-dot
                data-still={still ? 'true' : 'false'}
                aria-hidden="true"
              />
            ) : (
              <span
                className={s.spinner}
                data-still={still ? 'true' : 'false'}
                aria-hidden="true"
              />
            )}
            <p className={s.status} role="status">
              {phase === 'starting' ? STARTING : phase === 'recording' ? RECORDING : FINISHING}
            </p>
            {phase === 'recording' && (
              <>
                <span className={s.timer}>
                  <span className="visually-hidden">Time recorded: </span>
                  <span>{clock(elapsed)}</span>
                </span>
                <span ref={levels} className={s.levels} aria-hidden="true">
                  {STILL.map((transform, i) => (
                    <span key={i} className={s.levelBar} data-level-bar style={{ transform }} />
                  ))}
                </span>
              </>
            )}
          </div>
          <div className={s.hint}>
            <p className={s.say}>Describe what happened, or what would help</p>
            <p className={s.note}>Transcribed by Azure AI Speech. Only the words are kept.</p>
            {timeLeft !== null && <p className={s.note}>{timeLeft}</p>}
          </div>
          <div className={s.actions}>
            {phase === 'recording' && (
              <Button variant="cyan" onPress={onDone} icon={<Icon name="check" size={16} />}>
                Done
              </Button>
            )}
            {/* Even while finishing: the words heard so far go to the review at once. */}
            <Button
              variant="ghost-on-dark"
              onPress={onTypeInstead}
              icon={<Icon name="keyboard" size={16} />}
            >
              Type instead
            </Button>
            <AriaButton className={s.cancel} aria-label="Cancel feedback" onPress={onCancel}>
              <Icon name="close" size={18} />
            </AriaButton>
          </div>
        </div>
      </div>
    </section>
  );
}
